import { type User } from '@prisma/client'
import {
	json,
	type LoaderFunctionArgs,
	type MetaFunction,
} from '@remix-run/node'
import {
	Outlet,
	useLoaderData,
	useLocation,
	useNavigate,
	useSearchParams,
} from '@remix-run/react'
import { cellToBoundary, latLngToCell } from 'h3-js'
import { Maximize2Icon, Minimize2Icon } from 'lucide-react'
import posthog from 'posthog-js'
import { useCallback, useEffect, useRef, useState } from 'react'
import Konami from 'react-konami-code'
import { type MapRef } from 'react-map-gl'

import { Toaster } from 'sonner'
import { useDebounceCallback, useResizeObserver } from 'usehooks-ts'
import { Footer } from '#app/components/footer'
import { Header } from '#app/components/header'
import HydrationZustand from '#app/components/hydration-zustand'
import { Map } from '#app/components/map/map'
import { Button } from '#app/components/ui/button'
import { Sheet, SheetContent } from '#app/components/ui/sheet'
import { HIGHLIGHT_ZOOM, RESERVED_ROUTES } from '#app/consts'
import {
	getFavoritePlotGroupsByUserId,
	getFavoritePlotsByUserId,
} from '#app/models/favorite/get-favorite-plots'
import {
	getMediumClusterPlotIds,
	getPlotOrPlotGroupByHandle,
	getPlotsByResolution2,
	getPlotsByUserHandle,
	getPlotsByUserId,
	getSmallClusterPlotIds,
} from '#app/models/plot/get-plot'
import { StoreProvider, useStoreContext } from '#app/store'
import { type FetchedPlot, type FetchedPlotGroup } from '#app/types'
import { authenticator } from '#app/utils/auth.server'
import {
	useFlyToGroupPlot,
	useFlyToPlot,
	type FlySheetType,
} from '#app/utils/map'
import { cn } from '#app/utils/misc'
import { commitSession, getSession } from '#app/utils/session.server.js'

export const shouldRevalidate = () => false

type ViewState = {
	latitude: number
	longitude: number
	zoom: number
	bearing: number
	pitch: number
}

export type LayoutLoaderData = {
	user: User | null
	mapboxAccessToken: string
	googleMapsApiKey: string
	userPlots: FetchedPlot[]
	profilePlots: FetchedPlot[]
	viewportPlots: FetchedPlot[]
	favoritePlots: FetchedPlot[]
	favoritePlotGroups: FetchedPlotGroup[]
	smallClusterPlotIds: string[]
	mediumClusterPlotIds: string[]
	// largeClusterPlotIds: string[]
	initialViewState: ViewState
}

export async function loader({ request }: LoaderFunctionArgs) {
	const url = new URL(request.url)
	const pathname = url.pathname
	const isHomePage = pathname === '/'

	// TODO (low) cache these?
	// const smallClusterPlotIds = await getAllSmallClusterPlotIds()
	let smallClusterPlotIds
	let mediumClusterPlotIds

	let centerPlot

	let profilePlots
	let userPlots
	let favoritePlots
	let favoritePlotGroups
	let viewportPlots

	const defaultPlot = '8a190dc72b4ffff' //nessie
	const defaultLatLng = cellToBoundary(defaultPlot, true)[0]

	// TODO (low) default to a plot instead
	// default to London
	let initialViewState = {
		latitude: defaultLatLng[1],
		longitude: defaultLatLng[0],
		zoom: HIGHLIGHT_ZOOM,
		bearing: 0,
		pitch: 0,
	}

	// 1. if on a plot page, center the map on the plot
	// check if the route is either /plot/<handle> or /plot/edit/<handle> or /plot/edit-group/<handle>
	const plotHandleRegex = /^\/plot\/([a-zA-Z0-9_]+)(?:\/edit)?$/
	const plotEditHandleRegex = /^\/plot\/edit\/([a-zA-Z0-9_]+)$/
	const plotEditGroupHandleRegex = /^\/plot\/edit-group\/([a-zA-Z0-9_]+)$/
	const plotHandleMatch =
		pathname.match(plotHandleRegex) ||
		pathname.match(plotEditHandleRegex) ||
		pathname.match(plotEditGroupHandleRegex)
	const isPlotPage = !!plotHandleMatch

	if (plotHandleMatch) {
		const plotHandle = plotHandleMatch[1]
		const plot = await getPlotOrPlotGroupByHandle(plotHandle)
		if (plot && 'plotId' in plot) {
			centerPlot = plot

			const center = cellToBoundary(plot.plotId, true)[0]
			initialViewState = {
				...initialViewState,
				latitude: center[1],
				longitude: center[0],
			}
		}

		if (plot && 'plots' in plot) {
			if (plot.plots.length > 0) {
				const center = cellToBoundary(plot.plots[0].plotId, true)[0]
				initialViewState = {
					...initialViewState,
					latitude: center[1],
					longitude: center[0],
				}
			}
		}
	}

	// 2. if on a profile page, center the map on the users most recent plot
	// TODO (low) this would be better if we could center on the user's Plots bounds or at least some of them
	const pathnameWithoutSlash = pathname.slice(1)
	const isProfilePage =
		!RESERVED_ROUTES.includes(pathnameWithoutSlash) && !isHomePage
	if (isProfilePage) {
		const profileRegex = /^\/([a-zA-Z0-9_]+)$/
		const profileMatch = pathname.match(profileRegex)

		if (profileMatch) {
			const userHandle = profileMatch[1]

			// get the owned plots for the profile
			profilePlots = (await getPlotsByUserHandle(userHandle)) as FetchedPlot[]
			if (profilePlots.length > 0) {
				// center on the most recently created plot
				centerPlot = profilePlots[profilePlots.length - 1]
				const center = cellToBoundary(centerPlot.plotId, true)[0]

				// The center point of the combined plots
				initialViewState = {
					...initialViewState,
					latitude: center[1],
					longitude: center[0],
				}
			}
		}
	}

	// 3. if logged in and not on a plot or profile page, center the map on the user's Plots bounds
	const session = await authenticator.isAuthenticated(request)

	if (session) {
		// get the users owned Plots
		userPlots = (await getPlotsByUserId(session.id)) as FetchedPlot[]

		// center on the most recently created plot if not on plot or profile page
		if (!isProfilePage && !isPlotPage && userPlots.length) {
			centerPlot = userPlots[userPlots.length - 1]
			const center = cellToBoundary(centerPlot?.plotId, true)[0]

			// The center point of the combined plots
			initialViewState = {
				...initialViewState,
				latitude: center[1],
				longitude: center[0],
			}
		}

		const favoritePlotRelations = await getFavoritePlotsByUserId(session.id)
		favoritePlots = favoritePlotRelations.map(
			relation => relation.plot,
		) as FetchedPlot[]

		const favoritePlotGroupRelations = await getFavoritePlotGroupsByUserId(
			session.id,
		)
		favoritePlotGroups = favoritePlotGroupRelations.map(
			relation => relation.group,
		) as FetchedPlotGroup[]
	}

	if (centerPlot) {
		viewportPlots = await getPlotsByResolution2([centerPlot.resolution2PlotId])
	} else {
		const defaultCenter = latLngToCell(
			initialViewState.latitude,
			initialViewState.longitude,
			2,
		)
		viewportPlots = await getPlotsByResolution2([defaultCenter])
	}

	const allPlots = [
		...(userPlots || []),
		...(profilePlots || []),
		...(favoritePlots || []),
		...(viewportPlots || []),
	]

	const allResolution2Plots = Array.from(
		new Set(allPlots.map(plot => plot.resolution2PlotId)),
	)

	smallClusterPlotIds = await getSmallClusterPlotIds(allResolution2Plots)
	mediumClusterPlotIds = await getMediumClusterPlotIds(allResolution2Plots)

	// const signup = await fetch('https://app.thegoodapi.com/signup', {
	// 	method: 'POST',
	// 	headers: {
	// 		'Content-Type': 'application/json',
	// 		beta_token: 'KkeuYD2P6',
	// 	},
	// 	body: JSON.stringify({
	// 		email: 'chris@erda.app',
	// 		contact_name: 'Chris',
	// 		name: 'Erda',
	// 	}),
	// })

	// console.log(await signup.json())

	// 4. if not logged in, check if referred by a user
	let headers: HeadersInit = {}
	if (!session) {
		const referralCode = url.searchParams.get('rc')
		const cookieSession = await getSession(request.headers.get('Cookie'))

		if (referralCode) {
			cookieSession.set('referralCode', referralCode)

			headers = {
				'Set-Cookie': await commitSession(cookieSession),
			}
		}
	}

	return json<LayoutLoaderData>(
		{
			user: session,
			mapboxAccessToken: process.env.MAPBOX_TOKEN!,
			googleMapsApiKey: process.env.GOOGLE_MAPS_API_KEY!,
			initialViewState,
			userPlots: userPlots || [],
			profilePlots: profilePlots || [],
			favoritePlots: favoritePlots || [],
			favoritePlotGroups: favoritePlotGroups || [],
			viewportPlots: viewportPlots || [],
			smallClusterPlotIds:
				(smallClusterPlotIds?.map(
					plot => plot.resolution7PlotId,
				) as string[]) || [],

			mediumClusterPlotIds:
				(mediumClusterPlotIds?.map(
					plot => plot.resolution5PlotId,
				) as string[]) || [],
		},
		{
			headers,
		},
	)
}

export const meta: MetaFunction = () => [
	{
		script:
			'https://maps.googleapis.com/maps/api/js?key=AIzaSyD1q0xqu9V91pH8KzaBkmhzwQBFL4EX4Ug&libraries=places',
		defer: true,
	},
	{ title: 'Own or Gift a Plot & plant trees for $2 | Erda' },
	{
		name: 'description',
		content: `Own or Gift a Plot for $2 and contribute to sustainability. Each owned Plot helps plant two trees in developing communities, combating poverty and environmental harm.`,
	},
	{
		name: 'keywords',
		content:
			'erda, app, own, gift, digital, plots, piece, world, map, plant, tree, helps, hunger, poverty, environmental, sustainability, sentimental, meaningful, unique, instant',
	},
	{
		name: 'og:title',
		content: 'Own or Gift a Plot & plant trees for $2 | Erda',
	},
	{ name: 'og:type', content: 'website' },
	{ name: 'og:url', content: 'https://erda.app' },
	{
		name: 'og:image',
		content: 'https://erda.app/image/home.png',
	},
	{ name: 'og:card', content: 'summary_large_image' },
	{ name: 'og:creator', content: '@erda_app' },
	{ name: 'og:site', content: 'https://erda.app' },
	{
		name: 'og:description',
		content: `Own or Gift a Plot for $2 and contribute to sustainability. Each owned Plot helps plant two trees in developing communities, combating poverty and environmental harm.`,
	},
	{
		'twitter:image': 'https://erda.app/image/home.png',
	},
	{ name: 'twitter:card', content: 'summary_large_image' },
	{ name: 'twitter:creator', content: '@erda_app' },
	{
		name: 'twitter:title',
		content: 'Own or Gift a Plot & plant trees for $2 | Erda',
	},
	{
		name: 'twitter:description',
		content: `Own or Gift a Plot for $2 and contribute to sustainability. Each owned Plot helps plant two trees in developing communities, combating poverty and environmental harm.`,
	},
]

export type LayoutContextType = {
	mapRef: React.MutableRefObject<MapRef | null>
	flyToPlot: (plot: string, sheetType?: FlySheetType) => void
	flyToGroupPlot: (groupId: string, sheetType?: FlySheetType) => void
}

export default function Layout() {
	const location = useLocation()

	const {
		mapboxAccessToken,
		userPlots,
		profilePlots,
		favoritePlots,
		favoritePlotGroups,
		viewportPlots,
		smallClusterPlotIds,
		mediumClusterPlotIds,
		initialViewState,
		user,
	} = useLoaderData() as LayoutLoaderData

	useEffect(() => {
		if (!user) return

		posthog.identify(user.id, {
			email: user.email,
			name: user.name,
			handle: user.handle,
			created_at: user.createdAt,
		})
	}, [user])

	// remove any duplicate plots. consider any plot with the same plotId as a duplicate
	const combinedPlots = [
		...userPlots,
		...profilePlots,
		...favoritePlots,
		...viewportPlots,
	]
	const loadedPlots = Array.from(
		new Set(combinedPlots.map(plot => plot.plotId)),
	).map(plotId => {
		return combinedPlots.find(plot => plot.plotId === plotId)!
	})

	const isPlot =
		location.pathname.includes('/plot/') && !location.pathname.includes('/edit')
	const shouldCollapseSheet = isPlot

	return (
		<HydrationZustand>
			<StoreProvider
				loadedPlots={loadedPlots}
				smallClusterPlotIds={smallClusterPlotIds}
				mediumClusterPlotIds={mediumClusterPlotIds}
				viewState={{
					...initialViewState,
					padding: {
						top: 0,
						bottom: 0,
						left: 0,
						right: 0,
					},
				}}
				favoritePlotIds={favoritePlots.map(plot => plot.plotId)}
				favoritePlotGroupIds={
					favoritePlotGroups.map(group => group.id) as string[]
				}
				isSheetExpanded={!shouldCollapseSheet}
			>
				<LayoutInner {...{ mapboxAccessToken, initialViewState }} />
			</StoreProvider>
		</HydrationZustand>
	)
}

export function LayoutInner({
	mapboxAccessToken,
}: {
	mapboxAccessToken: string
}) {
	const navigate = useNavigate()
	const location = useLocation()

	const [searchParams] = useSearchParams()
	const screenshot = searchParams.get('screenshot')

	const debugMode = useStoreContext(s => s.debugMode)
	const setDebugMode = useStoreContext(s => s.setDebugMode)
	const setViewStatePadding = useStoreContext(s => s.setViewStatePadding)
	const setHighlightedPlotIds = useStoreContext(s => s.setHighlightedPlotIds)
	const setHighlightedPlotGroupIds = useStoreContext(
		s => s.setHighlightedPlotGroupIds,
	)
	const isSheetExpanded = useStoreContext(s => s.isSheetExpanded)
	const setIsSheetExpanded = useStoreContext(s => s.setIsSheetExpanded)

	const clearHighlighted = useCallback(() => {
		setHighlightedPlotIds([])
		setHighlightedPlotGroupIds([])
	}, [setHighlightedPlotGroupIds, setHighlightedPlotIds])

	const isHome = location.pathname === '/'
	const isCheckout = location.pathname === '/checkout'

	const [isDrawerOpen, setIsDrawerOpen] = useState(!isHome)
	// const [drawerSnapPoints] = useState<(string | number)[]>([1])
	// const [drawerSnap, setDrawerSnap] = useState<number | string | null>(1)
	const [canDismissDrawer, setCanDismissDrawer] = useState(!isCheckout)

	useEffect(() => {
		setIsDrawerOpen(location.pathname !== '/')
		setCanDismissDrawer(location.pathname !== '/checkout')

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location.pathname])

	const isClosing = useRef(false)

	useEffect(() => {
		if (isClosing.current) return

		const isPlot =
			location.pathname.includes('/plot/') &&
			!location.pathname.includes('/edit')

		setIsSheetExpanded(!isPlot)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location])

	const onClose = useCallback(() => {
		if (!canDismissDrawer) return
		isClosing.current = true

		clearHighlighted()
		navigate('/')
		setTimeout(() => {
			isClosing.current = false
		}, 100)
	}, [canDismissDrawer, clearHighlighted, navigate])

	const containerRef = useRef<HTMLDivElement>(null)
	const [drawerDirection, setDrawerDirection] = useState<'bottom' | 'right'>(
		'right',
	)
	const [toastPosition, setToastPosition] = useState<
		'bottom-center' | 'top-center'
	>('bottom-center')

	const onResize = (size: { width?: number; height?: number }) => {
		if (size.width && size.width < 768) {
			setDrawerDirection('bottom')
			setToastPosition('top-center')
			// TODO (low) allow scrolling when small snap point is active
			// setDrawerSnapPoints(['420px', 1])
			// setDrawerSnap('420px')
		} else {
			setDrawerDirection('right')
			setToastPosition('bottom-center')
			// setDrawerSnapPoints([1])
			// setDrawerSnap(1)
		}
	}
	const debouncedOnResize = useDebounceCallback(onResize, 100)
	useResizeObserver({
		ref: containerRef,
		onResize: debouncedOnResize,
	})

	useEffect(() => {
		onResize({ width: window.innerWidth })
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const mapRef = useRef<MapRef>(null)

	const flyToGroupPlot = useFlyToGroupPlot()
	const flyToPlot = useFlyToPlot()

	useEffect(() => {
		setViewStatePadding(
			isDrawerOpen
				? drawerDirection === 'bottom'
					? { bottom: 0 }
					: { right: 480 }
				: { top: 0, bottom: 0, left: 0, right: 0 },
		)
	}, [isDrawerOpen, drawerDirection, setViewStatePadding])

	const onToggleSheetExpanded = useCallback(() => {
		setIsSheetExpanded(!isSheetExpanded)
		posthog.capture('toggle_sheet_expanded', { expanded: !isSheetExpanded })
	}, [isSheetExpanded, setIsSheetExpanded])

	return (
		<div
			className={cn(
				'mx-auto flex h-[100dvh] flex-col items-center',
				screenshot && 'screenshot h-[1080px]',
			)}
		>
			{!screenshot && <Header />}
			<main
				className="flex h-full w-full flex-col items-center justify-center"
				ref={containerRef}
			>
				<Map mapRef={mapRef} mapboxAccessToken={mapboxAccessToken} />
				{/* <Drawer
          modal={!canDismissDrawer}
          direction={drawerDirection}
          open={isDrawerOpen}
          onOpenChange={(open) => {
            if (!canDismissDrawer) return setIsDrawerOpen(true)
            setIsDrawerOpen(open)
          }}
          onClose={onClose}
          snapPoints={drawerSnapPoints}
          activeSnapPoint={drawerSnap}
          setActiveSnapPoint={setDrawerSnap}
          dismissible={canDismissDrawer}>
          <DrawerContent
            showOverlay={!canDismissDrawer}
            forceMount
            onInteractOutside={(e) => e.preventDefault()}
            onKeyDown={(e) => {
              if (!canDismissDrawer) return

              if (e.key === 'Escape') {
                setIsDrawerOpen(false)
              }
            }}>
            <div className="overflow-overlay relative flex h-full w-full flex-col gap-4 overflow-auto rounded-lg px-4 py-6">
              <Outlet
                context={{
                  mapRef,
                  flyToPlot: (plotId: string, zoom?: number) =>
                    flyToPlot(plotId, mapRef, viewStatePadding, zoom),
                  flyToGroupPlot: (groupId: string, zoom?: number) =>
                    flyToGroupPlot(groupId, mapRef, viewStatePadding, zoom),
                }}
              />
            </div>
          </DrawerContent>
        </Drawer> */}
				{!screenshot && (
					<>
						<Sheet
							modal={!canDismissDrawer}
							open={isDrawerOpen}
							onOpenChange={open => {
								if (!canDismissDrawer) return setIsDrawerOpen(true)

								if (!open) {
									onClose()
								} else {
									setIsDrawerOpen(open)
								}
							}}
						>
							<SheetContent
								canDismiss={canDismissDrawer}
								side={drawerDirection}
								className={cn(
									'inset-x-0 bottom-0 z-50 mx-auto flex h-[calc(50dvh-80px)] max-h-[calc(100dvh-120px)] w-full max-w-[480px] flex-col items-stretch gap-4 rounded-lg rounded-b-none border !p-0 outline-none',
									'gap-1 transition-all md:inset-x-auto md:inset-y-0 md:right-0 md:my-16 md:h-auto md:max-h-none md:w-[480px] md:rounded-b-lg md:rounded-r-none',
									isSheetExpanded && 'h-[calc(100dvh-120px)]',
								)}
								onOpenAutoFocus={e => e.preventDefault()}
								onInteractOutside={e => e.preventDefault()}
								onKeyDown={e => {
									if (!canDismissDrawer) return
									if (e.key === 'Escape') {
										setIsDrawerOpen(false)
										onClose()
									}
								}}
							>
								<div
									className={cn(
										'overflow-overlay flex flex-1 flex-col items-stretch gap-4 overflow-y-auto px-4 py-6',
									)}
								>
									<Button
										variant="unstyled"
										className="absolute -top-10 left-3 flex !h-8 items-center justify-center rounded-full border bg-white !px-2 text-xs text-foreground ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none md:hidden"
										onClick={onToggleSheetExpanded}
									>
										{isSheetExpanded && (
											<>
												<Minimize2Icon className="h-4 w-4" />
												<span>Collapse</span>
											</>
										)}
										{!isSheetExpanded && (
											<>
												<Maximize2Icon className="h-4 w-4" />
												<span>Expand</span>
											</>
										)}
									</Button>
									<Outlet
										context={{
											mapRef,
											flyToPlot: (plotId: string, sheetState?: FlySheetType) =>
												flyToPlot(plotId, mapRef, sheetState),
											flyToGroupPlot: (
												groupId: string,
												sheetState?: FlySheetType,
											) => flyToGroupPlot(groupId, mapRef, sheetState),
										}}
									/>
								</div>
							</SheetContent>
						</Sheet>
						<Footer />
						<Toaster position={toastPosition} />
						<Konami action={() => setDebugMode(!debugMode)} />
					</>
				)}
			</main>
		</div>
	)
}
